import { useMemo, useState } from 'react';
import {
  BellRinging,
  ChartBar,
  CheckCircle,
  MagnifyingGlass,
  ShieldWarning,
  Warning,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

import { useCustomerSummaries } from '@/features/customers';
import {
  useConsentPreferences,
  useEnqueueReminders,
  useSetConsentPreference,
  type ReminderCandidate,
  type ReminderChannel,
} from '@/features/reminders';
import { useCurrentBranch } from '@/shared/context/TenantContext';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { formatDateTime } from '@/shared/utils/formatters';

const CHANNELS: ReminderChannel[] = ['whatsapp', 'sms', 'email', 'push'];
const CHANNEL_LABELS: Record<ReminderChannel, string> = {
  whatsapp: 'WhatsApp',
  sms: 'SMS',
  email: 'E-posta',
  push: 'Push',
};

export function RemindersPage(): JSX.Element {
  const branch = useCurrentBranch();
  const enqueue = useEnqueueReminders();
  const { data: customers } = useCustomerSummaries();
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const { data: consents, isLoading: consentsLoading } = useConsentPreferences(
    selectedCustomerId || undefined,
  );
  const setConsent = useSetConsentPreference(selectedCustomerId || undefined);

  const selectedCustomer = useMemo(
    () => customers?.find((customer) => customer.id === selectedCustomerId) ?? null,
    [customers, selectedCustomerId],
  );

  const queueItems = enqueue.data ?? [];
  const hasRun = enqueue.data !== undefined;
  const summary = useMemo(() => summarizeQueue(queueItems), [queueItems]);

  const handleEnqueue = async () => {
    if (!branch?.branchId) return;
    try {
      await enqueue.mutateAsync(branch.branchId);
      toast.success('Hatırlatmalar kuyruğa alındı');
    } catch {
      toast.error('Hatırlatmalar kuyruğa alınamadı');
    }
  };

  return (
    <div className="px-biz-card-lg py-biz-content-lg max-w-[1400px] mx-auto flex flex-col gap-biz-content-lg">
      <header className="rounded-biz-card border border-biz-border bg-gradient-to-br from-biz-surface to-biz-muted/40 p-biz-card-lg flex items-start sm:items-center justify-between gap-biz-content-lg flex-wrap">
        <div className="flex items-start gap-biz-content-lg min-w-0">
          <div className="h-12 w-12 rounded-biz-btn bg-biz-primary text-biz-primary-foreground flex items-center justify-center shrink-0">
            <BellRinging size={24} weight="fill" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h1 className="text-biz-2xl font-bold biz-heading">Hatırlatmalar</h1>
            <p className="text-biz-sm text-biz-muted-foreground mt-1">
              Yaklaşan randevular için opt-in kontrollü WhatsApp/SMS/e-posta kuyruğu.
            </p>
            {hasRun ? (
              <p className="text-biz-xs text-biz-muted-foreground mt-1">
                Son çalıştırma sonucu: {summary.queued} kuyrukta, {summary.skipped} atlandı.
              </p>
            ) : null}
          </div>
        </div>
        <Button
          type="button"
          onClick={handleEnqueue}
          loading={enqueue.isPending}
          leftIcon={<BellRinging size={18} />}
        >
          24s hatırlatmaları hazırla
        </Button>
      </header>

      <ReminderSummaryCards summary={summary} hasRun={hasRun} />

      <section className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-biz-content-lg">
        <Card className="overflow-hidden">
          <CardHeader>
            <div>
              <h2 className="font-semibold biz-heading">Kuyruk sonucu</h2>
              <p className="text-biz-sm text-biz-muted-foreground">
                Backend `biz_notification_log` içine queued/skipped kayıtları yazar.
              </p>
            </div>
          </CardHeader>
          <div className="divide-y divide-biz-border">
            {enqueue.isPending ? <QueueSkeleton /> : null}
            {!enqueue.isPending
              ? queueItems.map((item) => (
                  <div
                    key={`${item.bookingId}-${item.channel}`}
                    className="px-biz-card py-biz-content-lg flex items-center justify-between gap-biz-content-lg"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-biz-content flex-wrap">
                        <p className="font-semibold truncate">{item.customerName}</p>
                        <ChannelBadge channel={item.channel} />
                      </div>
                      <p className="text-biz-sm text-biz-muted-foreground">
                        {formatDateTime(item.startsAt)} · {item.templateKey}
                      </p>
                      {item.skipReason ? (
                        <p className="text-biz-xs text-biz-warning mt-1">
                          {item.skipReason}
                        </p>
                      ) : null}
                    </div>
                    <StatusBadge
                      variant={item.status === 'queued' ? 'success' : 'warning'}
                      label={item.status === 'queued' ? 'Kuyrukta' : 'Atlandı'}
                    />
                  </div>
                ))
              : null}
            {!enqueue.isPending && !hasRun ? (
              <div className="px-biz-card py-biz-card text-biz-sm text-biz-muted-foreground">
                Henüz kuyruk çalıştırılmadı.
              </div>
            ) : null}
            {!enqueue.isPending && hasRun && queueItems.length === 0 ? (
              <div className="px-biz-card py-biz-card">
                <div className="rounded-biz-card border border-biz-success-border bg-biz-success-subtle px-biz-content-lg py-biz-content">
                  <p className="font-semibold text-biz-success">Gönderilecek hatırlatma yok</p>
                  <p className="text-biz-sm text-biz-muted-foreground mt-1">
                    Bu zaman penceresinde opt-in koşullarını sağlayan yaklaşan randevu bulunmadı.
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        </Card>

        <Card>
          <CardHeader>
            <div>
              <h2 className="font-semibold biz-heading">Müşteri izinleri</h2>
              <p className="text-biz-sm text-biz-muted-foreground">
                Kanal bazlı opt-in/opt-out tercihi.
              </p>
            </div>
          </CardHeader>
          <CardBody className="flex flex-col gap-biz-content">
            <div className="relative">
              <MagnifyingGlass
                size={18}
                className="absolute left-biz-content-lg top-1/2 -translate-y-1/2 text-biz-muted-foreground"
                aria-hidden="true"
              />
              <select
                value={selectedCustomerId}
                onChange={(event) => setSelectedCustomerId(event.target.value)}
                className="w-full min-h-biz-touch pl-10 pr-biz-content-lg rounded-biz-input border border-biz-input bg-biz-background"
              >
                <option value="">Müşteri seç</option>
                {(customers ?? []).map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.fullName}
                  </option>
                ))}
              </select>
            </div>

            {selectedCustomer ? (
              <div className="flex flex-col gap-biz-content">
                <p className="text-biz-sm text-biz-muted-foreground">
                  {selectedCustomer.fullName} için kanal izinleri
                </p>
                {consentsLoading ? <ConsentSkeleton /> : null}
                {!consentsLoading ? CHANNELS.map((channel) => {
                  const pref = consents?.find((item) => item.channel === channel);
                  const optIn = pref?.optIn ?? true;
                  return (
                    <button
                      key={channel}
                      type="button"
                      onClick={() => setConsent.mutate({ channel, optIn: !optIn })}
                      disabled={setConsent.isPending}
                      className="min-h-biz-touch rounded-biz-btn border border-biz-border px-biz-content-lg py-biz-content flex items-center justify-between text-left disabled:opacity-70"
                    >
                      <span>
                        <span className="font-medium block">{CHANNEL_LABELS[channel]}</span>
                        {pref?.updatedAt ? (
                          <span className="text-biz-xs text-biz-muted-foreground">
                            {pref.source} · {formatDateTime(pref.updatedAt)}
                          </span>
                        ) : null}
                      </span>
                      <span className="inline-flex items-center gap-2 text-biz-sm font-medium">
                        {optIn ? (
                          <CheckCircle size={18} className="text-biz-success" weight="fill" />
                        ) : (
                          <Warning size={18} className="text-biz-warning" weight="fill" />
                        )}
                        {optIn ? 'Opt-in' : 'Opt-out'}
                      </span>
                    </button>
                  );
                }) : null}
              </div>
            ) : (
              <div className="rounded-biz-card border border-biz-border bg-biz-muted/30 px-biz-content-lg py-biz-content text-biz-sm text-biz-muted-foreground">
                Bir müşteri seçerek kanal izinlerini yönetin.
              </div>
            )}
          </CardBody>
        </Card>
      </section>
    </div>
  );
}

function summarizeQueue(items: ReminderCandidate[]) {
  const channelMix = CHANNELS.reduce<Record<ReminderChannel, number>>((acc, channel) => {
    acc[channel] = 0;
    return acc;
  }, {} as Record<ReminderChannel, number>);

  let queued = 0;
  let skipped = 0;
  let consentBlocked = 0;

  for (const item of items) {
    channelMix[item.channel] += 1;
    if (item.status === 'queued') {
      queued += 1;
    } else {
      skipped += 1;
      if (item.skipReason?.toLowerCase().includes('consent')) {
        consentBlocked += 1;
      }
    }
  }

  return { queued, skipped, consentBlocked, channelMix };
}

function ReminderSummaryCards({
  summary,
  hasRun,
}: {
  summary: ReturnType<typeof summarizeQueue>;
  hasRun: boolean;
}): JSX.Element {
  const total = CHANNELS.reduce((sum, channel) => sum + summary.channelMix[channel], 0);

  return (
    <section className="grid grid-cols-2 lg:grid-cols-4 gap-biz-content">
      <SummaryCard tone="success" icon={<CheckCircle size={18} weight="fill" />} label="Kuyrukta" value={hasRun ? summary.queued : 0} />
      <SummaryCard tone="warning" icon={<Warning size={18} weight="fill" />} label="Atlandı" value={hasRun ? summary.skipped : 0} />
      <SummaryCard tone="warning" icon={<ShieldWarning size={18} weight="fill" />} label="İzin blokajı" value={hasRun ? summary.consentBlocked : 0} />
      <Card>
        <CardBody className="p-biz-content">
          <div className="flex items-center gap-2 text-biz-xs font-semibold uppercase tracking-wide text-biz-muted-foreground">
            <ChartBar size={16} aria-hidden="true" />
            Kanal dağılımı
          </div>
          <div className="mt-biz-content space-y-2">
            {CHANNELS.map((channel) => {
              const value = hasRun ? summary.channelMix[channel] : 0;
              const width = total === 0 ? 0 : Math.round((value / total) * 100);
              return (
                <div key={channel} className="grid grid-cols-[72px_1fr_24px] items-center gap-2 text-biz-xs">
                  <span className="text-biz-muted-foreground">{CHANNEL_LABELS[channel]}</span>
                  <div className="h-1.5 rounded-full bg-biz-muted overflow-hidden">
                    <div className="h-full rounded-full bg-biz-primary" style={{ width: `${width}%` }} />
                  </div>
                  <span className="text-right biz-numeric font-semibold">{value}</span>
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>
    </section>
  );
}

function SummaryCard({
  tone,
  icon,
  label,
  value,
}: {
  tone: 'success' | 'warning';
  icon: JSX.Element;
  label: string;
  value: number;
}): JSX.Element {
  const toneClass =
    tone === 'success'
      ? 'bg-biz-success-subtle text-biz-success border-biz-success-border'
      : 'bg-biz-warning-subtle text-biz-warning border-biz-warning-border';

  return (
    <Card>
      <CardBody className="p-biz-content">
        <div className="flex items-center gap-2 text-biz-xs font-semibold uppercase tracking-wide text-biz-muted-foreground">
          <span className={`h-8 w-8 rounded-full border flex items-center justify-center ${toneClass}`}>
            {icon}
          </span>
          {label}
        </div>
        <p className="text-biz-2xl font-bold biz-heading biz-numeric mt-biz-content">{value}</p>
      </CardBody>
    </Card>
  );
}

function ChannelBadge({ channel }: { channel: ReminderChannel }): JSX.Element {
  return (
    <span className="rounded-full border border-biz-border bg-biz-muted/40 px-2 py-0.5 text-biz-xs font-medium text-biz-muted-foreground">
      {CHANNEL_LABELS[channel]}
    </span>
  );
}

function QueueSkeleton(): JSX.Element {
  return (
    <>
      {[0, 1, 2].map((item) => (
        <div key={item} className="px-biz-card py-biz-content-lg flex items-center justify-between gap-biz-content-lg">
          <div className="space-y-2 flex-1">
            <div className="h-4 w-48 rounded-full bg-biz-muted animate-pulse" />
            <div className="h-3 w-72 max-w-full rounded-full bg-biz-muted animate-pulse" />
          </div>
          <div className="h-7 w-20 rounded-full bg-biz-muted animate-pulse" />
        </div>
      ))}
    </>
  );
}

function ConsentSkeleton(): JSX.Element {
  return (
    <>
      {CHANNELS.map((channel) => (
        <div key={channel} className="min-h-biz-touch rounded-biz-btn border border-biz-border px-biz-content-lg py-biz-content">
          <div className="h-4 w-24 rounded-full bg-biz-muted animate-pulse" />
          <div className="h-3 w-36 rounded-full bg-biz-muted animate-pulse mt-2" />
        </div>
      ))}
    </>
  );
}
